import { default as CancelIcon } from '@mui/icons-material/Cancel'
import { default as CheckCircleIcon } from '@mui/icons-material/CheckCircle'
import { default as CloseIcon } from '@mui/icons-material/Close'
import { default as WarningIcon } from '@mui/icons-material/Warning'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { Suspense, useState } from 'react'
import { toast } from 'react-toastify'
import { downloadPdfReports } from 'src/clinic/views/clinicBrandPayoutReports/services/exportServices'
import { ConfirmationModal } from 'src/consumer/components/helperComponents/dynamic'
import { MentorUpcomingPastEventsTable } from 'src/mentor/components/MentorEventsPage/MentorEventsComponentsAndHelpers'
import {
  getPriceStringWithCurrency,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { prepareBookedEventsDataWithConflictingInvoices } from 'src/supportPortal/views/payoutReports/invoiceConflictUtils'

const InvoiceConflictDialog = ({ open, onClose, invoice, paymentInfo, onMarkAsPaid, onCancel }) => {
  const [loadingConflicts, setLoadingConflicts] = useState(false)
  const [loading, setLoading] = useState(false)
  const [conflicts, setConflicts] = useState([])
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const [showPaidConfirmation, setShowPaidConfirmation] = useState(false)

  // Check for conflicts when dialog opens
  React.useEffect(() => {
    if (open && invoice && paymentInfo) {
      checkForConflicts()
    }
  }, [open, invoice, paymentInfo])

  const checkForConflicts = async () => {
    setLoadingConflicts(true)
    setLoading(true)
    try {
      // Note: This is a GET request with query params and POST body - using postSPDataWithParams
      const response = await getSPDataWithParams(
        `/api/secure/cc/support/invoices/payout-invoice/find-conflicts`,
        { 'invoice-id': invoice.id },
      )

      if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const conflictData = response.data?.data?.['booked-event-info'] || []
        const conflictingInvoices = response.data?.data?.['payout-invoice'] || []
        setConflicts(
          prepareBookedEventsDataWithConflictingInvoices(conflictData, conflictingInvoices),
        )
      } else {
        toast.error('Failed to check for conflicts')
        onClose()
      }
    } catch (error) {
      console.error('Error checking conflicts:', error)
      toast.error('Failed to check for conflicts')
      onClose()
    } finally {
      setLoading(false)
      setLoadingConflicts(false)
    }
  }

  const handleMarkAsPaid = () => {
    if (conflicts.length === 0) {
      // No conflicts, proceed directly
      onMarkAsPaid()
    } else {
      // Show confirmation for marking as paid with conflicts
      setShowPaidConfirmation(true)
    }
  }

  const handleCancelAndRegenerate = () => {
    setShowCancelConfirmation(true)
  }

  const confirmMarkAsPaid = () => {
    setShowPaidConfirmation(false)
    onMarkAsPaid()
  }

  const confirmCancelAndRegenerate = async () => {
    setShowCancelConfirmation(false)
    setLoading(true)

    try {
      // Step 1: Generate new invoice (excluding conflicted events)
      const blacklistedEvents = conflicts.map((conflict) => conflict.id || conflict._id)

      const invoiceRequest = {
        targetEntityId: invoice.targetEntityId,
        targetEntityType: invoice.targetEntityType,
        invoiceFrom: invoice.invoiceFrom,
        invoiceTo: invoice.invoiceTo,
        // Add other required fields based on your InvoicePDFRequest structure
      }

      const queryParams = new URLSearchParams({
        'event-type': conflicts?.[0]?.eventType,
        'send-razorpay-notifications': 'false',
        'send-razorpay-reminder-notifications': 'false',
        'show-tds-reduction': 'false',
      })

      // Add blacklisted events as separate params
      queryParams.append('blacklisted-events', blacklistedEvents.join(','))

      const generateResponse = await downloadPdfReports({
        entityId: invoice.targetEntityId,
        entityType: invoice.targetEntityType,
        exportEventType: conflicts?.[0]?.eventType,
        sendRazorpayNotifications: false,
        sendRazorpayReminderNotifications: false,
        addTdsRelatedInfo: false,
        reportDateMoment: dayjs(invoice.invoiceFrom),
        reportEndDateMoment: dayjs(invoice.invoiceTo),
        blacklistedEventIds: blacklistedEvents,
      })

      if (!!generateResponse) {
        // Step 2: Mark current invoice as cancelled
        const cancelResponse = await getSPDataWithParams(
          `/api/secure/cc/support/invoices/payout-invoice/mark-cancelled`,
          { 'invoice-id': invoice.id },
        )

        if (cancelResponse.status === 200 && isCommonResponseSuccessfulV2(cancelResponse)) {
          toast.success('Invoice cancelled and new invoice generated successfully')
          onCancel() // Refresh the parent component
          onClose()
        } else {
          toast.error('Failed to cancel original invoice')
        }
      } else {
        toast.error('Failed to generate new invoice')
      }
    } catch (error) {
      console.error('Error in cancel and regenerate:', error)
      toast.error('Failed to cancel and regenerate invoice')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <WarningIcon color="warning" />
              <Typography variant="h6">Invoice Conflict Resolution</Typography>
            </Box>
            <IconButton onClick={handleClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Invoice Information */}
          <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Invoice Details
            </Typography>
            <Typography variant="body2">
              <strong>Invoice Number:</strong> {invoice?.invoiceNumber}
            </Typography>
            <Typography variant="body2">
              <strong>Period:</strong>{' '}
              {invoice?.invoiceFrom
                ? dayjs.utc(invoice.invoiceFrom).local().format('DD MMM YYYY')
                : '-'}{' '}
              to{' '}
              {invoice?.invoiceTo
                ? dayjs.utc(invoice.invoiceTo).local().format('DD MMM YYYY')
                : '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Amount Generated:</strong>{' '}
              {invoice?.payoutAmountGenerated
                ? getPriceStringWithCurrency(invoice.payoutAmountGenerated)
                : '--'}
            </Typography>
          </Box>

          {loadingConflicts ? (
            <Box display="flex" justifyContent="center" p={4}>
              <Typography>Checking for conflicts...</Typography>
            </Box>
          ) : conflicts.length === 0 ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body1">
                No conflicts found! This invoice can be marked as paid safely.
              </Typography>
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Conflicts Detected!</strong>
                </Typography>
                <Typography variant="body2">
                  The following {conflicts.length} booked event(s) have conflicts that may affect
                  this invoice:
                </Typography>
              </Alert>

              <Divider sx={{ my: 2 }} />

              {/* Conflicts Table */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Conflicted Events:
                </Typography>
                <MentorUpcomingPastEventsTable
                  allEvents={conflicts}
                  rightSideCustomComponent={({ event }) => {
                    return (
                      <div className="d-flex gap-1">
                        <Typography variant="body2" fontWeight={600}>Conflicting Invoice:</Typography>
                        <Typography variant="body2">
                          {event?.conflictingInvoice?.invoiceNumber}
                        </Typography>
                      </div>
                    )
                  }}
                  loadingSlots={false}
                  showMentorName
                  hideRefreshButton={true}
                />
                {/* <ConsultsTab
                  bookedEventsListWithPatientInfo={conflicts}
                  loadingTableData={false}
                  showActionButtons={false}
                /> */}
              </Box>

              <Divider sx={{ my: 3 }} />

              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Options:</strong>
                </Typography>
                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                  • <strong>Mark as Paid:</strong> Proceed with payment despite conflicts
                </Typography>
                <Typography variant="body2" component="div">
                  • <strong>Cancel & Regenerate:</strong> Cancel this invoice and create a new one
                  excluding the conflicted events
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading} variant="outlined">
            Close
          </Button>

          {conflicts.length > 0 && (
            <Button
              onClick={handleCancelAndRegenerate}
              disabled={loading}
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
            >
              Cancel & Regenerate
            </Button>
          )}

          <Button
            onClick={handleMarkAsPaid}
            disabled={loading}
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
          >
            {conflicts.length > 0 ? 'Mark as Paid (with conflicts)' : 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal for Mark as Paid with Conflicts */}
      <Suspense fallback={null}>
        <ConfirmationModal
          visibility={showPaidConfirmation}
          visibilitySetter={setShowPaidConfirmation}
          action="Mark as Paid"
          body={
            <div>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to mark this invoice as paid despite the conflicts?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                There are {conflicts.length} conflicted event(s) that may require attention.
              </Typography>
            </div>
          }
          buttonColor="warning"
          onSubmitFunctions={[confirmMarkAsPaid]}
        />
      </Suspense>

      {/* Confirmation Modal for Cancel and Regenerate */}
      <Suspense fallback={null}>
        <ConfirmationModal
          visibility={showCancelConfirmation}
          visibilitySetter={setShowCancelConfirmation}
          action="Cancel & Regenerate"
          body={
            <div>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to cancel this invoice and generate a new one?
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This will:
              </Typography>
              <Typography variant="body2" component="div" sx={{ ml: 2 }}>
                • Mark the current invoice as cancelled
              </Typography>
              <Typography variant="body2" component="div" sx={{ ml: 2 }}>
                • Generate a new invoice excluding {conflicts.length} conflicted event(s)
              </Typography>
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                This action cannot be undone.
              </Typography>
            </div>
          }
          buttonColor="error"
          onSubmitFunctions={[confirmCancelAndRegenerate]}
        />
      </Suspense>
    </>
  )
}

export default InvoiceConflictDialog
